import { Component, OnDestroy } from "@angular/core";
import { environment } from "../../environments/environment";
import { BaseClass } from "../shared/classes/base";
import { ToastController, AlertController } from "@ionic/angular";
import { TranslationService } from "../shared/services/translation.service";
import { ProcessesService } from "./processes.service";
import { ProcessTypeEnum } from "../shared/interfaces/process.interface";
import { Router } from "@angular/router";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { VersionPreview } from "./_interfaces/version-preview.interface";
import { ConfigService } from "../shared/services/multi-clients/config.service";

@Component({
    selector: "app-processes",
    templateUrl: "./processes.page.html",
})
export class ProcessesPage extends BaseClass implements OnDestroy {
    public processAvailability;

    public processes = {};
    public displayedProcesses = {};

    public ProcessTypeEnum = ProcessTypeEnum;
    public processTypeKeys = Object.keys(ProcessTypeEnum).filter((key) => !isNaN(Number(key)));
    private _searchSubject: Subject<string> = new Subject();

    constructor(
        protected _toastController: ToastController,
        protected _translationService: TranslationService,
        private _alertController: AlertController,
        private _processesService: ProcessesService,
        private _router: Router,
        private _configService: ConfigService
    ) {
        super(_toastController, _translationService);
        this._setSearchSubscription();
        this.processAvailability = this._configService.settings.features.processAvailability;
    }

    ionViewWillEnter() {
        this._loadProcesses();
    }

    public goToManageVersion(version: VersionPreview) {
        const url = `/manage-version/${version.type}/${version.id ?? 0}`;
        this._router.navigateByUrl(url);
    }

    public updateSearch(searchTextValue: string): void {
        this._searchSubject.next(searchTextValue);
    }

    public async confirmDeleteProcess(processId: number): Promise<void> {
        const alert = await this._alertController.create({
            message: this._translationService.getTranslation("processes.confirmDeleteProcess"),
            buttons: [
                {
                    text: this._translationService.getTranslation("generic.no"),
                    role: "cancel",
                    cssClass: "danger",
                },
                {
                    text: this._translationService.getTranslation("generic.yes"),
                    handler: () => {
                        this._deleteProcess(processId);
                    },
                },
            ],
        });

        await alert.present();
    }

    public async cloneProcess(processId: number) {
        const alert = await this._alertController.create({
            message: this._translationService.getTranslation("processes.confirmProcessCopy"),
            buttons: [
                {
                    text: this._translationService.getTranslation("generic.no"),
                    role: "cancel",
                    cssClass: "danger",
                },
                {
                    text: this._translationService.getTranslation("generic.yes"),
                    handler: () => {
                        this._processesService.cloneProcess(processId).subscribe(
                            () => {
                                this._loadProcesses();
                                this.notifySuccess("processes.cloneSuccess");
                            },
                            (error) => this.notifyError(error),
                        );
                    },
                },
            ],
        });

        await alert.present();
    }

    private _loadProcesses(): void {
        this._processesService.getProcesses().subscribe(
            (response) => {
                this.processes[ProcessTypeEnum.GoalInputs] = [];
                this.processes[ProcessTypeEnum.FreeEvaluationMidYear] = [];
                this.processes[ProcessTypeEnum.GoalsReview] = [];
                this.processes[ProcessTypeEnum.TwoPlusTwoCoaching] = [];
                this.processes[ProcessTypeEnum.ThreeSixtyEvaluation] = [];
                this.processes[ProcessTypeEnum.FreeEvaluationEndYear] = [];
                this.processes[ProcessTypeEnum.ThreeSixtyEvaluation] = [];
                this.processes[ProcessTypeEnum.SelfEvaluationMidYear] = [];
                this.processes[ProcessTypeEnum.SelfEvaluation] = [];
                this.processes[ProcessTypeEnum.ManagerEvaluation] = [];
                this.processes[ProcessTypeEnum.HeadEvaluation] = [];
                this.processes[ProcessTypeEnum.EvaluationCalibration] = [];
                this.processes[ProcessTypeEnum.FinalResult] = [];
                this.processes[ProcessTypeEnum.FinalFeedback] = [];
                this.processes[ProcessTypeEnum.GenericProcess] = [];

                response.data.forEach((process) => {
                    if (this.processes.hasOwnProperty(process.type)) {
                        this.processes[process.type].push(process);
                    }
                });

                this.displayedProcesses = Object.assign({}, this.processes);
            },
            (error) => this.notifyError(error),
        );
    }

    private _deleteProcess(processId: number): void {
        this._processesService.deleteProcess(processId).subscribe(
            () => {
                this._loadProcesses();
                this.notifySuccess("generic.deleteSuccess");
            },
            (error) => this.notifyError(error),
        );
    }

    private _setSearchSubscription() {
        this._searchSubject.pipe(distinctUntilChanged(), debounceTime(1000)).subscribe((searchValue: string) => {
            if (searchValue) {
                Object.keys(this.processes).forEach((key) => {
                    this.displayedProcesses[key] = this.processes[key].filter((process) => {
                        return process.name.toLowerCase().includes(searchValue.toLowerCase().trim());
                    });
                });
            } else {
                this.displayedProcesses = Object.assign({}, this.processes);
            }
        });
    }

    ngOnDestroy() {
        this._searchSubject.unsubscribe();
    }
}
